import React, { useEffect, useState } from "react";
import { Modal, Input, Button, Spin, message } from "antd";
import { useDuplicateConfigurationMutation } from "../../../services/ApiSlice.ts";
import "./DuplicateModal.css"

interface ConfigurationDisplayDto {
  id: string;
  displayName: string;
}

interface DuplicateModalProps {
  configurationId: string;
  visible: boolean;
  onClose: () => void;
  onDuplicateSuccess?: () => void;
  configurationList: ConfigurationDisplayDto[];
}

const suggestDuplicateName = (baseName: string, existingNames: string[]): string => {
  const copyPattern = /^(.*?)(?:\s*\(Copy-\d+\))?$/i;
  const match = copyPattern.exec(baseName);
  if (match) {
    baseName = match[1].trim();
  }

  let copyIndex = 1;
  let suggestedName = `${baseName} (Copy-${copyIndex})`;
  while (existingNames.includes(suggestedName)) {
    copyIndex++;
    suggestedName = `${baseName} (Copy-${copyIndex})`;
  }

  return suggestedName;
};

const DuplicateModal: React.FC<DuplicateModalProps> = ({ configurationId, visible, onClose, onDuplicateSuccess, configurationList }) => {

  const [name, setName] = useState("");

  useEffect(() => {
    if (visible && configurationList.length > 0) {
      const currentConfig = configurationList.find(c => c.id === configurationId);
      if (currentConfig) {
        const existingNames = configurationList.map(c => c.displayName);
        const suggested = suggestDuplicateName(currentConfig.displayName, existingNames);
        setName(suggested);
      }
    }
  }, [visible, configurationList, configurationId]);

  const [duplicateConfiguration, { isLoading: isDuplicating }] = useDuplicateConfigurationMutation();

  const handleDuplicate = async () => {
    if (!name.trim()) {
      message.error("Please enter a valid name");
      return;
    }

    try {
      await duplicateConfiguration({ id: configurationId, newName: name.trim() }).unwrap();
      message.success("Configuration copied successfully");
      onDuplicateSuccess?.();
      onClose();
    } catch (error) {
      message.error("Failed to copy configuration");
    }
  };

  return (

    <Modal
      className="duplicate-modal"
      title="Copy Configuration"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="duplicate" type="primary" loading={isDuplicating} onClick={handleDuplicate}>Copy</Button>,
        <Button key="cancel" onClick={onClose} disabled={isDuplicating}>Cancel</Button>,
      ]}
      
    >
      {configurationList.length === 0 ? (
        <div className="duplicate-modal-spinner">
          <Spin />
        </div>
      ) : (
         <div className = "duplicate-modal-text">
          <p>Enter a name for the configuration:</p>
          <Input value={name} onChange={e => setName(e.target.value)} />
        </div>
      )}
    </Modal>
  );
};

export default DuplicateModal;
